import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { allProducts } from '../../mocks/product-detail';

const ProductPage = () => {
  const { id } = useParams<{ id: string }>();
  const product = (id && allProducts[id]) ? allProducts[id] : null;

  const [selectedImage, setSelectedImage] = useState(0);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 pt-48 pb-16 text-center">
          <i className="ri-shopping-bag-line text-6xl text-gray-300 mb-4 block"></i>
          <h2 className="text-2xl font-bold text-gray-700 mb-2">找不到此產品</h2>
          <p className="text-gray-500 mb-6">產品 ID「{id}」不存在或已下架</p>
          <Link to="/collections" className="inline-block bg-gray-900 text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
            瀏覽所有產品
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-7xl mx-auto px-4 pt-36 pb-8">
        {/* 麵包屑 */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-8">
          <Link to="/" className="hover:text-gray-900 transition-colors">首頁</Link>
          <span>/</span>
          <Link to={`/collections/${product.category}`} className="hover:text-gray-900 transition-colors">
            {product.categoryName}
          </Link>
          <span>/</span>
          <span className="text-gray-900">{product.name}</span>
        </div>

        {/* 產品詳情 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* 左側：圖片 */}
          <div className="space-y-4">
            <div className="relative w-full h-[600px] bg-gray-50 rounded-lg overflow-hidden">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover object-top"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://jc-kidss.com.hk/cdn/shop/files/placeholder.jpg';
                }}
              />
            </div>

            {product.images.length > 1 && (
              <div className="grid grid-cols-5 gap-2">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative w-full h-24 bg-gray-50 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      selectedImage === index ? 'border-gray-900' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 右側：產品資訊 */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
              <p className="text-sm text-gray-500">產品編號：{product.sku}</p>
            </div>

            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-red-600">HK${product.price}</span>
            </div>

            <div className="border-t border-b border-gray-200 py-4">
              <p className="text-gray-700 leading-relaxed text-sm">{product.description}</p>
            </div>

            {/* 顏色展示 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">顏色</label>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((color) => (
                  <span
                    key={color}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white whitespace-nowrap"
                  >
                    {color}
                  </span>
                ))}
              </div>
            </div>

            {/* 尺碼展示 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">尺碼</label>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <span
                    key={size}
                    className="w-16 h-12 flex items-center justify-center border border-gray-300 rounded-lg text-sm text-gray-700 bg-white"
                  >
                    {size}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">韓國童裝尺碼，建議參考尺碼表選擇</p>
            </div>

            {/* 產品特點 */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {['現貨即日發貨', '批發六件起批'].map((feat) => (
                <div key={feat} className="flex items-center gap-2 text-sm text-gray-700">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            {/* 產品資料 */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">產品資料</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex"><span className="w-24 text-gray-500">材質：</span><span>{product.material}</span></div>
                <div className="flex"><span className="w-24 text-gray-500">產地：</span><span>{product.origin}</span></div>
                <div className="flex"><span className="w-24 text-gray-500">洗滌方式：</span><span>{product.care}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* 相關產品 */}
        {product.relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">相關產品</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-product-shop>
              {product.relatedProducts.map((item) => (
                <Link
                  key={item.id}
                  to={`/product/${item.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative w-full h-64 bg-gray-50 rounded-lg overflow-hidden mb-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    {item.badge && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                        {item.badge}
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1 group-hover:text-gray-600 transition-colors line-clamp-2">
                    {item.name}
                  </h3>
                  <p className="text-base font-bold text-red-600">HK${item.price}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductPage;
